import { ASPECT_RATIO_OPTIONS, DURATION_OPTIONS, RESOLUTION_OPTIONS } from "./components/VideoProject/FieldsConstant";
import { VIDEO_GENERATION_STEPS_CONSTANTS, VIDEO_LENGTH } from "./constant";
import VideoIcon from "@icons/video/video-concept.svg";
import { BaseFileId, RAI_STATUS, RAIResponse } from "@/types";
import { HandleFileRAIResponseParams, HandleRAIResponseParams, HandleRAIResponseResult, ImageFrameUrl, RaiFieldError } from "./types";
import { RaiValidationPayload } from "../generate-lifestyle/components/StepTwo/types";
import { ASSET_TYPES, workflowType } from "@/constants/constants";
import { getDefaultStringValue } from "@/lib/utils";
import { RaiFailedCompliancesProps } from "@/components/RaiErrorAccordion";

export const getVideoGenerationPageName = (stepNumber: number): string | undefined =>
  VIDEO_GENERATION_STEPS_CONSTANTS.STEP_NAMES[stepNumber as keyof typeof VIDEO_GENERATION_STEPS_CONSTANTS.STEP_NAMES];

export const getVideoSpecsData = (resolution: string, aspectRatio: string, duration: string) => {
  return [
    {
      icon: VideoIcon,
      text: "Size",
      value: aspectRatio,
    },
    {
      text: "Length",
      value: duration ? `:0${duration} seconds` : VIDEO_LENGTH,
    },
    {
      text: "Resolution",
      value: resolution,
    },
  ];
};

export const getResolution = (resolution: string) => {
  const resolutionArr = RESOLUTION_OPTIONS?.filter((item) => item.label === resolution);
  return resolutionArr?.length ? resolutionArr[0] : RESOLUTION_OPTIONS[0];
};

export const getAspectRatio = (ratio: string) => {
  const ratioArr = ASPECT_RATIO_OPTIONS?.filter((item) => item.label === ratio);
  return ratioArr?.length ? ratioArr[0] : ASPECT_RATIO_OPTIONS[0];
};

export const getDuration = (duration: number) => {
  const durationArr = DURATION_OPTIONS?.filter((item) => item.id === String(duration));
  return durationArr?.length ? durationArr[0] : DURATION_OPTIONS[0];
};

export const buildVideoInputFromRequest = (videoRequest?: { prompt?: string; firstFrame?: string; lastFrame?: string }) => {
  if (!videoRequest) return {};

  return {
    ...(videoRequest.prompt && { description: videoRequest.prompt }),
    ...(videoRequest.firstFrame && {
      firstFrameImage: {
        url: videoRequest.firstFrame,
        fileId: "",
        fileName: "",
      },
      isCheckedFirstFrame: true,
    }),
    ...(videoRequest.lastFrame && {
      lastFrameImage: {
        url: videoRequest.lastFrame,
        fileId: "",
        fileName: "",
      },
      isCheckedLastFrame: true,
    }),
  };
};

export const handleFileRAIResponse = async ({ url, handleRaiRequest, getRAIResponseStatus, raiCache }: HandleFileRAIResponseParams) => {
  const payload: RaiValidationPayload[] = [
    {
      assetUrl: url,
      assetType: ASSET_TYPES.IMAGE,
    },
  ];
  const currentFlow = workflowType.VIDEO_GENERATION;
  const raiResponse = await handleRaiRequest(payload, currentFlow, raiCache);
  let raiError: RaiFieldError;
  let isRAISuccess = false;

  if (raiResponse?.[0]?.status === "FAIL") {
    raiError = getRAIResponseStatus(raiResponse, ASSET_TYPES.IMAGE, undefined, true);
  }

  if (raiResponse?.[0]?.status === "PASS") {
    isRAISuccess = true;
  }

  return {
    raiResponse,
    raiError,
    fileRaiResponse: raiResponse?.[0],
    isRAISuccess,
  };
};

const categorizeRaiResponses = (raiResponse: RAIResponse[] | undefined) => {
  const textRaiResponses: RAIResponse[] = [];
  const imageRaiResponses: RAIResponse[] = [];

  raiResponse?.forEach((item: RAIResponse) => {
    if (item.assetType === ASSET_TYPES.TEXT) {
      textRaiResponses.push(item);
    } else if (item.assetType === ASSET_TYPES.IMAGE) {
      imageRaiResponses.push(item);
    }
  });

  return { textRaiResponses, imageRaiResponses };
};

const processRaiResponse = (
  response: RAIResponse | undefined,
  assetType: string,
  fieldName: string,
  getRAIResponseStatus: (
    raiResponse: RAIResponse[],
    type: string,
    field?: string,
    isNewFeature?: boolean,
  ) => string | RaiFailedCompliancesProps[],
  setFieldError: (field: string, error: string) => void,
  shouldProcess: boolean = true,
): { error?: RaiFieldError } => {
  if (!shouldProcess || !response) {
    return {};
  }

  let error: RaiFieldError | undefined;

  if (response.status === RAI_STATUS.FAIL) {
    error = getRAIResponseStatus([response], assetType, undefined, true);
    if (typeof error === "string") {
      setFieldError(fieldName, error);
    }
  }

  return { error };
};

const getFrameRaiResponse = (
  imageRaiResponses: RAIResponse[],
  frameIndex: number,
  frameUrl: string | undefined,
): RAIResponse | undefined => {
  const hasValidIndex = frameIndex >= 0;
  const hasFrameUrl = !!frameUrl;

  if (hasValidIndex && hasFrameUrl) {
    return imageRaiResponses[frameIndex];
  }
};

export const handleRAIResponseUtil = async ({
  data,
  handleRaiRequest,
  raiCache,
  getRAIResponseStatus,
  setFieldError,
}: HandleRAIResponseParams): Promise<HandleRAIResponseResult> => {
  const payload: RaiValidationPayload[] = [
    {
      assetText: data.videoDescription,
      assetType: ASSET_TYPES.TEXT,
    },
  ];

  // Only add image payloads if URLs exist
  processImagePayload(data.firstFrameUrl, payload);
  processImagePayload(data.lastFrameUrl, payload);

  const currentFlow = workflowType.VIDEO_GENERATION;
  const raiResponse = await handleRaiRequest(payload, currentFlow, raiCache);

  const { textRaiResponses, imageRaiResponses: initialImageRaiResponses } = categorizeRaiResponses(raiResponse);
  let imageRaiResponses = initialImageRaiResponses;

  if (data.firstFrameUrl?.raiOutput) {
    imageRaiResponses = [data.firstFrameUrl?.raiOutput, ...imageRaiResponses];
  }

  if (data.lastFrameUrl?.raiOutput) {
    imageRaiResponses = [...imageRaiResponses, data.lastFrameUrl?.raiOutput];
  }

  const descriptionResult = processRaiResponse(
    textRaiResponses[0],
    ASSET_TYPES.TEXT,
    "videoDescription",
    getRAIResponseStatus,
    setFieldError,
  );

  let firstFrameImageIndex = 0;
  let lastFrameImageIndex = 1;

  // If only one image URL is provided, adjust indices
  if (data.firstFrameUrl?.url && !data.lastFrameUrl?.url) {
    lastFrameImageIndex = -1; // No last frame
  } else if (!data.firstFrameUrl?.url && data.lastFrameUrl?.url) {
    firstFrameImageIndex = -1; // No first frame
    lastFrameImageIndex = 0;
  }

  const firstFrameResult = processRaiResponse(
    firstFrameImageIndex >= 0 ? imageRaiResponses[firstFrameImageIndex] : undefined,
    ASSET_TYPES.IMAGE,
    "firstFrameUrl",
    getRAIResponseStatus,
    setFieldError,
    !!data.firstFrameUrl?.url,
  );

  const lastFrameResult = processRaiResponse(
    lastFrameImageIndex >= 0 ? imageRaiResponses[lastFrameImageIndex] : undefined,
    ASSET_TYPES.IMAGE,
    "lastFrameUrl",
    getRAIResponseStatus,
    setFieldError,
    !!data.lastFrameUrl?.url,
  );

  return {
    raiResponse,
    descriptionRaiError: descriptionResult.error,
    firstFrameRaiError: firstFrameResult.error,
    lastFrameRaiError: lastFrameResult.error,
    VideoRAIResponse: {
      description: textRaiResponses?.[0],
      firstFrame: getFrameRaiResponse(imageRaiResponses, firstFrameImageIndex, data.firstFrameUrl?.url),
      lastFrame: getFrameRaiResponse(imageRaiResponses, lastFrameImageIndex, data.lastFrameUrl?.url),
    },
  };
};

const processImagePayload = (frameUrl: ImageFrameUrl | undefined, payload: RaiValidationPayload[]) => {
  if (frameUrl?.url && !frameUrl?.raiOutput) {
    payload.push({
      assetUrl: frameUrl.url,
      assetType: ASSET_TYPES.IMAGE,
      ...(frameUrl.fileId ? { mediaId: frameUrl.fileId } : {}),
    });
  }
};

export const stripQueryAndHash = (value: string) => value.split("?")[0].split("#")[0];

export const stripExtension = (value: string) => value.replace(/\.[^./]+$/, "");

export const getVideoFileName = (videoUrl: string) => {
  const sanitizedVideoUrl = stripQueryAndHash(videoUrl);
  const pathSegments = sanitizedVideoUrl.split("/");
  const rawFileName = pathSegments.at(-1) ?? "";
  return stripExtension(rawFileName);
};

export const getImageData = (file: BaseFileId) => {
  return {
    ...getPayloadImageData(file),
    base64: getDefaultStringValue(file?.base64),
  };
};

export const getPayloadImageData = (file: BaseFileId) => {
  return {
    url: getDefaultStringValue(file?.url),
    fileId: getDefaultStringValue(file?.fileId),
    fileName: getDefaultStringValue(file?.fileName),
  };
};

export const getRaiInfoStatus = (videoStatus?: RAI_STATUS | null, transcriptStatus?: RAI_STATUS | null): boolean | undefined => {
  if (videoStatus === RAI_STATUS.PASS && transcriptStatus === RAI_STATUS.PASS) {
    return true;
  }
  if (videoStatus === RAI_STATUS.FAIL || transcriptStatus === RAI_STATUS.FAIL) {
    return false;
  }
  return false;
};

export const hasValidPermission = (roles: string[], requiredPermission: string): boolean => {
  return roles?.includes(requiredPermission);
};
